<template>
    <div>
        <div class="loader" v-if="loading"></div>
        <div v-if="!registerEntityId">
            {{ $t('budget.needSavedEvent') }}
        </div>
        <div v-else>
            <budget-report-expenses
                :register-entity-id="registerEntityId"
                :expenses-total-costs="total"
            />
            <table class="table table-bordered table-responsive" v-if="budget.items && budget.items.length > 0">
                <thead>
                    <tr>
                        <th v-if="!isLockedForEdit"></th>
                        <th>{{ $t('budget.group') }}</th>
                        <th>{{ $t('budget.tariffItem') }}</th>
                        <th>{{ $t('budget.description') }}</th>
                        <th>{{ $t('budget.date') }}</th>
                        <th>{{ $t('budget.quantity') }}</th>
                        <th>{{ $t('budget.singlePrice') }}</th>
                        <th>{{ $t('budget.total') }}</th>
                        <th>{{ $t('filesUpload') }}</th>
                        <th>{{ $t('lastUpdate') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <budget-planning-row v-for="(item, index) in budget.items" :item="item" :show-file-upload="true"
                        :key="index"
                        :index="index"
                        @totalChanged="totalChanged"
                        @onItemUploadFilesClicked="onItemUploadFilesClicked"
                        @clickedItemIndex="clickedItemIndex"
                        @deleteItem="deleteItem"
                        :is-locked-for-edit="isLockedForEdit"
                        @fileToBeRemoved="fileToRemoveFromGrid">
                    </budget-planning-row>
                </tbody>
                <tfoot>
                <tr>
                    <td colspan="10" class="font-weight-bold">{{ $t('budget.total') }}: {{ total }}</td>
                </tr>
                </tfoot>
            </table>
            <div v-if="canWrite && !isLockedForEdit">
                <button class="btn btn-primary" @click="addRow">{{ $t('add') }}</button>
                <button class="btn btn-primary" @click="onSave">{{ $t('save') }}</button>
                <button class="btn btn-primary float-right" @click.stop="loadData">{{ $t('buttons.load.btnText') }}</button>
            </div>
        </div>

        <!-- File upload modal -->
        <div v-if="showFileUploadModal && !isLockedForEdit" id="fileUploadModal" class="modal" tabindex="-1" role="dialog"
             data-backdrop="static">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('filesUpload') }}</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close"
                                @click="closeFileUploadModal">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="form-row">
                            <vs-upload ref="fileUpload"
                                :key="uploadKey"
                                :showUploadButton="false"
                                :text="$t('addFile')"
                                @on-delete="fileRemoved"
                                @change="fileAdded" />
                        </div>

                        <div class="form-row">
                            <div class="form-group col-12">
                                <label class="font-weight-bold" :for="'reportingDocsSelector' + _uid">{{$t('fileSelection.reportingDocs')}}</label>
                                <dynamic-multi-select
                                    :apiUrl="`/api/events/${registerEntityId}/reportingDocsDropdownModels`"
                                    :id="'reportingDocsSelector' + _uid"
                                    :name="'reportingDocsSelector' + _uid"
                                    filter="contains"
                                    :height="500"
                                    @input="selectedReportingDocInput">
                                </dynamic-multi-select>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group col-12">
                                <label class="font-weight-bold" :for="'organizationalDocsSelector' + _uid">{{$t('fileSelection.organizationalDocs')}}</label>
                                <dynamic-multi-select
                                    :apiUrl="`/api/events/${registerEntityId}/organizationalDocsDropdownModels`"
                                    :id="'organizationalDocsSelector' + _uid"
                                    :name="'organizationalDocsSelector' + _uid"
                                    filter="contains"
                                    :height="500"
                                    @input="selectedOrgDocInput">
                                </dynamic-multi-select>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal"
                                @click="closeFileUploadModal">{{ $t('buttons.close') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import BudgetPlanningRow from './BudgetPlanningRow';
    import {
        BudgetPlanningModel,
        BudgetPlanningRowModel
    } from '@/models/models';
    import { BudgetReportFileModel } from '@/models/file.models';
    import BudgetReportExpenses from './BudgetReportExpenses';
    import DynamicMultiSelect from '@/components/dropdown/DynamicMultiSelect';
    import { mapGetters } from 'vuex';

    export default {
        components: {
            BudgetPlanningRow,
            BudgetReportExpenses,
            DynamicMultiSelect
        },
        props: {
            registerEntityId: {
                type: [Number, String],
                required: true
            },
            eventDateFrom: {
                type: [Date, String]
            },
            eventDateTo: {
                type: [Date, String]
            },
            canWrite: {
                type: Boolean,
                default: false
            },
            isLockedForEdit: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                budget: new BudgetPlanningModel({ registerEntityId: this.registerEntityId }),
                showFileUploadModal: true,
                fileUploadItemIndex: null,
                clickedFileItemIndex: null,
                itemsToDelete: [],
                total: 0,
                loading: false,
                uploadKey: 0
            }
        },
        computed: {
            ...mapGetters(['fileSizeLimit']),
        },
        methods: {
            loadData() {
                this.loading = true;
                this.$http.get(`/api/Expenses/GetByRegisterEntityId/${this.registerEntityId}`).then(result => {
                    if (result.data) {
                        this.budget = new BudgetPlanningModel(result.data);
                        this.calculateTotal();
                    }
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                }).then(() => { this.loading = false; });
            },
            addRow() {
                this.budget.items.push(new BudgetPlanningRowModel());
            },
            roundToSecondDigit(number) {
                return Math.round(number * 100) / 100;
            },
            totalChanged() {
                this.calculateTotal();
            },
            calculateTotal() {
                let totalSum = 0;
                this.budget.items.forEach(item => {
                    if (item.total) {
                    }
                    totalSum += item.total;
                });
                this.total = this.roundToSecondDigit(totalSum);
            },
            openFileUploadModal() {
                $('#fileUploadModal').modal();
            },
            closeFileUploadModal() {
                $('#fileUploadModal').modal('hide');
                setTimeout(() => { // Workaround за да се изчиства vuesax file upload компонентата
                    this.showFileUploadModal = true;
                }, 200);
            },
            onItemUploadFilesClicked(index) {
                this.fileUploadItemIndex = index;
                this.openFileUploadModal();
            },
            fileAdded() {
                const file = this.$refs.fileUpload.$refs.fileInput.files[0];

                if (file.size > this.fileSizeLimit) { // 10 MB
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$t('notifications.fileSizeLimitExceeded', { maxSize: this.fileSizeLimit / 1048576 }),
                        color: 'danger',
                    });

                    // Force re-render of the upload component by changing its key
                    this.uploadKey++;
                    return;
                }

                this.budget.items[this.fileUploadItemIndex].files.push(new BudgetReportFileModel({
                    fileName: file.name,
                    content: file,
                    budgetReportItemId: this.budget.items[this.fileUploadItemIndex].id
                }));
            },
            fileRemoved(fileToRemove) {
                const indexToDelete = this.budget.items[this.fileUploadItemIndex].files.findIndex(x => x.name === fileToRemove.name);
                this.budget.items[this.fileUploadItemIndex].files.splice(indexToDelete, 1);
            },
            fileToRemoveFromGrid(fileToRemove) {
                const indexToDelete = this.budget.items[this.clickedFileItemIndex].files.indexOf(fileToRemove);
                this.budget.items[this.clickedFileItemIndex].files.splice(indexToDelete, 1);
                if (fileToRemove.id) {
                    this.budget.fileIdsToDelete.push(fileToRemove.id);
                }
            },
            clickedItemIndex(index) {
                this.clickedFileItemIndex = index;
            },
            deleteItem(index) {
                const itemToDelete = this.budget.items[index];
                if (itemToDelete.id) {
                    this.itemsToDelete.push(itemToDelete.id);
                }
                this.budget.items.splice(index, 1);
            },
            onSave() {
                const expensesToSend = new BudgetPlanningModel({
                    registerEntityId: this.budget.registerEntityId
                });

                this.budget.items.forEach(item => {
                    let newItem = new BudgetPlanningRowModel(item);
                    newItem.files = [];
                    newItem.eventDocuments = [];

                    if (item.files) {
                        item.files.forEach(file => {
                            if (!file.id && (file.reportingDocId || file.orgDocId)) {
                                newItem.eventDocuments.push({
                                    id: file.id,
                                    reportingDocId: file.reportingDocId,
                                    orgDocId: file.orgDocId
                                });
                            }
                        })
                    }

                    expensesToSend.items.push(newItem);
                });

                expensesToSend.fileIdsToDelete = this.budget.fileIdsToDelete;
                expensesToSend.itemsToDelete = this.itemsToDelete;

                this.$http.post('/api/Expenses/CreateOrUpdate', expensesToSend).then(result => {
                    this.itemsToDelete = [];
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        color: 'success'
                    });
                    this.addIdsToNewItems(result.data);
                    this.uploadFiles();
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$t('notifications.errorDataNotSaved'),
                        color: 'danger'
                    });
                });
            },
            addIdsToNewItems(responseData) {
                if (responseData && responseData.items) {
                    this.budget.items.forEach(item => {
                        if (!item.id) {
                            const responseItem = responseData.items.find(i => i.uid === item.uid);
                            if (responseItem) {
                                item.id = responseItem.id;
                            }
                        }
                    });
                }
            },
            uploadFiles() {
                this.loading = true;
                let files = [];
                this.budget.items.forEach(item => {
                    item.files.forEach(file => {
                        if (!file.id && !file.reportingDocId && !file.orgDocId) {
                            file.budgetReportItemId = item.id;
                            files.push(file);
                        }
                    });
                });


                if (files.length > 0) {
                    const vm = this;
                    this.$http.all(this.uploadFilesAsync(vm, files)).then(result => {
                        this.loadData();
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successUploadingFiles'),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorUploadingFiles'),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .finally(() => { this.loading = false; });
                } else {
                    this.loading = false;
                }
            },
            uploadFilesAsync(vm, files) {
                let requests = [];
                files.forEach(function (file) {
                    let formData = new FormData();
                    formData.append('itemId', file.budgetReportItemId);
                    formData.append('files', file.content, file.fileName);
                    requests.push(vm.$http.post('/api/Expenses/UploadFiles', formData));
                });

                return requests;
            },
            selectedReportingDocInput(val, items) {
                if (!items) {
                    items = [];
                }

                const toAdd = items.filter(x => !this.budget.items[this.fileUploadItemIndex].files.some(f => f.reportingDocId === x.id));
                toAdd.forEach(file => {
                    this.budget.items[this.fileUploadItemIndex].files.push(new BudgetReportFileModel({
                        fileName: file.item.fileName,
                        budgetReportItemId: this.budget.items[this.fileUploadItemIndex].id,
                        reportingDocId: file.id
                    }));
                });

                if (this.budget.items && this.budget.items[this.fileUploadItemIndex].files) {
                    const toRemove = this.budget.items[this.fileUploadItemIndex].files.filter(x => x.reportingDocId && !items.some(f => f.id === x.reportingDocId));

                    toRemove.forEach(file => {
                        const indexToDelete = this.budget.items[this.fileUploadItemIndex].files.indexOf(file);
                        this.budget.items[this.fileUploadItemIndex].files.splice(indexToDelete, 1);
                    });
                }
            },
            selectedOrgDocInput(val, items) {
                if (!items) {
                    items = [];
                }

                const toAdd = items.filter(x => !this.budget.items[this.fileUploadItemIndex].files.some(f => f.orgDocId === x.id));
                toAdd.forEach(file => {
                    this.budget.items[this.fileUploadItemIndex].files.push(new BudgetReportFileModel({
                        fileName: file.item.fileName,
                        budgetReportItemId: this.budget.items[this.fileUploadItemIndex].id,
                        orgDocId: file.id
                    }));
                });

                if (this.budget.items && this.budget.items[this.fileUploadItemIndex].files) {
                    const toRemove = this.budget.items[this.fileUploadItemIndex].files.filter(x => x.orgDocId && !items.some(f => f.id === x.orgDocId));

                    toRemove.forEach(file => {
                        const indexToDelete = this.budget.items[this.fileUploadItemIndex].files.indexOf(file);
                        this.budget.items[this.fileUploadItemIndex].files.splice(indexToDelete, 1);
                    });
                }
            },
        },
        mounted() {
            this.loadData();
        },
    }
</script>
